import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  QuotationInboxDto,
  RejectQuotationInboxRequest,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export class QuotationInboxClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/quotations/inbox`, onRequestChange)
  }

  list(): Promise<QuotationInboxDto[]> {
    return this.GetAsync({ relativePath: '', id: 'ListQuotationInbox' })
  }

  detail(quotationId: string): Promise<QuotationInboxDto> {
    return this.GetAsync({
      relativePath: `/${quotationId}`,
      id: `GetQuotationInbox-${quotationId}`,
    })
  }

  accept(quotationId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/accept`,
      id: `AcceptQuotationInbox-${quotationId}`,
    })
  }

  reject(
    quotationId: string,
    request: RejectQuotationInboxRequest,
  ): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/reject`,
      data: request,
      id: `RejectQuotationInbox-${quotationId}`,
    })
  }

  changeDecisionChannel(
    quotationId: string,
    request: ChangeChannelRequest,
  ): Promise<QuotationInboxDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${quotationId}/decision-channel`,
      data: request,
      id: `ChangeQuotationDecisionChannel-${quotationId}`,
    })
  }

  upload(file: File, requestForQuotationId: string): Promise<unknown> {
    const data = new FormData()
    data.append('file', file)
    data.append('requestForQuotationId', requestForQuotationId)
    return this.PostAuthenticatedAsync({ relativePath: '/upload', data, id: 'UploadQuotationInbox' })
  }

  download(quotationId: string) {
    return this.DownloadAsync({ relativePath: `/${quotationId}/document`, id: `DownloadQuotationInbox-${quotationId}` })
  }

  downloadDecision(quotationId: string) {
    return this.DownloadAsync({ relativePath: `/${quotationId}/decision-document`, id: `DownloadQuotationDecision-${quotationId}` })
  }
}

export class QuotationInboxController extends BaseController<QuotationInboxClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new QuotationInboxClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(quotationId: string) {
    return this.handle(() => this.api.detail(quotationId))
  }

  accept(quotationId: string) {
    return this.handle(() => this.api.accept(quotationId))
  }

  reject(quotationId: string, reason?: string) {
    return this.handle(() => this.api.reject(quotationId, { reason }))
  }

  changeDecisionChannel(quotationId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeDecisionChannel(quotationId, request))
  }

  upload(file: File, requestForQuotationId: string) {
    return this.handle(() => this.api.upload(file, requestForQuotationId))
  }

  download(quotationId: string) {
    return this.handle(() => this.api.download(quotationId))
  }

  downloadDecision(quotationId: string) {
    return this.handle(() => this.api.downloadDecision(quotationId))
  }
}
